import React, { useEffect, useState } from "react";
import api, { API_BASE, formatErr } from "../../lib/api";

const cardStyle = { padding: 12, background: "var(--pf-surface)", border: "1px solid var(--pf-border)", borderRadius: 8 };
const labelStyle = { fontSize: 11, fontWeight: 700, color: "var(--pf-muted)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 };
const btnStyle = { padding: "8px 14px", fontSize: 12, fontWeight: 700, background: "var(--pf-ember)", color: "#fff", border: 0, borderRadius: 5, cursor: "pointer" };

export default function WeldsPanel({ projectId }) {
  const [welds, setWelds] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");

  const load = async () => {
    if (!projectId) return;
    try {
      const [w, d] = await Promise.all([
        api.get(`/projects/${projectId}/welds`),
        api.get(`/projects/${projectId}/welds/dashboard`),
      ]);
      setWelds(w.data); setDashboard(d.data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { load(); }, [projectId]);

  const autoDerive = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      const sample_lines = [{
        id: "L-100-001-1A1", size_in: 6, design_T_C: 250, rating_class: 300,
        pipe_class: { material: "ASTM A106 Gr B", schedule: "40", rating_class: 300 },
        "3d_routing": { waypoints: [
          { x: 0, y: 0, z: 1.5 }, { x: 25, y: 0, z: 1.5 }, { x: 25, y: 8, z: 1.5 }, { x: 25, y: 8, z: 6 },
        ] },
        branches: [{ x: 12, y: 0, z: 1.5, to: "T-101" }],
      }];
      const { data } = await api.post(
        `/projects/${projectId}/welds/auto-derive`,
        { lines: sample_lines, overwrite: true },
      );
      setMsg(`Derived ${data.derived_count} welds (${data.shop} shop, ${data.field} field) from sample 6″ Class 300 line.`);
      await load();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setBusy(false); }
  };

  // ── File 53: Generate NDE Plan ──
  const [ndePlan, setNdePlan] = useState(null);
  const generateNDE = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      const { data } = await api.post(`/projects/${projectId}/welds/nde-plan`);
      setNdePlan(data);
      setMsg(`NDE Plan generated — ${data.summary.total_welds} welds (${data.summary.high_criticality} HIGH · ${data.summary.medium_criticality} MED · ${data.summary.low_criticality} LOW · ${data.summary.RT_joints_required} RT · ${data.summary.PWHT_joints} PWHT).`);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {err && <div style={{ padding: 8, background: "rgba(239,68,68,.1)", color: "#FF8888", borderRadius: 5, fontSize: 12 }}>{err}</div>}
      {msg && <div style={{ padding: 8, background: "rgba(34,197,94,.1)", color: "#22c55e", borderRadius: 5, fontSize: 12 }}>{msg}</div>}

      {dashboard && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
          <KPI label="Total Welds"        value={dashboard.total_welds} />
          <KPI label="Shop Welds"         value={dashboard.shop_welds} accent="#3aa0ff" />
          <KPI label="Field Welds"        value={dashboard.field_welds} accent="#ff5252" />
          <KPI label="Acceptance Rate"    value={`${dashboard.acceptance_rate_pct}%`} />
          <KPI label="RT Progress"        value={`${dashboard.rt_progress_pct}%`} />
          <KPI label="Rejected"           value={dashboard.rejected} accent="#ef4444" />
        </div>
      )}

      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>Auto-Derive Weld Register</div>
          <button data-testid="welds-auto-derive" style={btnStyle} onClick={autoDerive} disabled={busy}>
            {busy ? "Working…" : "Auto-Derive (Sample Line)"}
          </button>
          <button data-testid="welds-nde-plan-btn"
                   style={{ ...btnStyle, background: "#16a34a" }}
                   onClick={generateNDE} disabled={busy}>
            Generate NDE Plan
          </button>
          <a data-testid="welds-map-svg" target="_blank" rel="noopener noreferrer"
             href={`${API_BASE}/projects/${projectId}/welds/map.svg`}
             style={{ ...btnStyle, background: "var(--pf-surface-2)", color: "var(--pf-fg)", border: "1px solid var(--pf-border)", textDecoration: "none" }}>
            Open Weld Map (SVG)
          </a>
        </div>
        <div style={{ fontSize: 11, color: "var(--pf-muted)", marginTop: 6 }}>
          One weld per joint between waypoints / fittings / spool breaks (transport length 12 m). Shop welds (fab) marked blue, field welds (erection) red. NDE % is auto-set by class/temperature (ASME B31.3 §341).
        </div>
      </div>

      {ndePlan && (
        <div style={cardStyle} data-testid="welds-nde-plan-result">
          <div style={{ ...labelStyle, marginBottom: 8 }}>
            NDE Plan — {ndePlan.summary.total_welds} welds (ASME B31.3 §341.3.2)
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, marginBottom: 10 }}>
            <KPI label="HIGH crit." value={ndePlan.summary.high_criticality} accent="#ef4444" />
            <KPI label="MED crit." value={ndePlan.summary.medium_criticality} accent="#f59e0b" />
            <KPI label="LOW crit." value={ndePlan.summary.low_criticality} accent="#22c55e" />
            <KPI label="RT required" value={ndePlan.summary.RT_joints_required} accent="#3aa0ff" />
            <KPI label="PWHT joints" value={ndePlan.summary.PWHT_joints} accent="#a855f7" />
          </div>
          <div style={{ maxHeight: 280, overflow: "auto" }}>
            <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "var(--pf-surface-2)", position: "sticky", top: 0 }}>
                {["Weld", "Line", "Crit.", "RT%", "UT%", "MT%", "PT%", "PWHT", "Heat-No", "WPS"].map((h) => <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {(ndePlan.nde_plan || []).slice(0, 50).map((p, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                    <td style={td}><strong style={{ color: "#F26B1F" }}>{p.weld_id}</strong></td>
                    <td style={td}>{p.line_number}</td>
                    <td style={{ ...td, color: p.criticality === "high_criticality" ? "#ef4444" : p.criticality === "medium_criticality" ? "#f59e0b" : "#22c55e" }}>
                      {p.criticality.replace("_criticality", "").toUpperCase()}
                    </td>
                    <td style={td}>{p.RT_pct}</td>
                    <td style={td}>{p.UT_pct}</td>
                    <td style={td}>{p.MT_pct}</td>
                    <td style={td}>{p.PT_pct}</td>
                    <td style={td}>{p.PWHT_required ? "Y" : "N"}</td>
                    <td style={td}>{p.heat_number_pipe || "—"}</td>
                    <td style={td}>{p.WPS_number || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 10, color: "var(--pf-muted)", marginTop: 8 }}>
            Codes: {(ndePlan.codes || []).join(" · ")}
          </div>
        </div>
      )}

      {welds.length > 0 && (
        <div style={cardStyle}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>Weld Register — {welds.length} welds</div>
          <div style={{ maxHeight: 360, overflow: "auto" }}>
            <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--pf-surface-2)", position: "sticky", top: 0 }}>
                  {["Weld ID", "Line", "Joint", "Type", "Shop/Field", "Size″", "Material", "WPS", "Status", "RT %"].map((h) => <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {welds.map((w) => (
                  <tr key={w.id} style={{ borderBottom: "1px solid var(--pf-border)" }}>
                    <td style={td}><strong style={{ color: "#F26B1F" }}>{w.weld_id}</strong></td>
                    <td style={td}>{w.line_number}</td>
                    <td style={td}>{w.joint_type}</td>
                    <td style={td}>{w.type}</td>
                    <td style={{ ...td, color: w.shop_or_field === "FIELD" ? "#ff5252" : "#3aa0ff", fontWeight: 700 }}>{w.shop_or_field}</td>
                    <td style={td}>{w.size_inch}</td>
                    <td style={td}>{w.material}</td>
                    <td style={td}>{w.wps_number}</td>
                    <td style={td}><StatusBadge status={w.status} /></td>
                    <td style={td}>{(w.nde_required || {}).RT}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const th = { padding: 6, textAlign: "left", color: "var(--pf-muted)", textTransform: "uppercase", fontSize: 9 };
const td = { padding: 6, fontFamily: "monospace" };

function KPI({ label, value, accent = "#F26B1F" }) {
  return (
    <div style={{ padding: 10, background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)", borderRadius: 6, borderLeft: `3px solid ${accent}` }}>
      <div style={{ fontSize: 9, color: "var(--pf-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  );
}
function StatusBadge({ status }) {
  const colors = { PLANNED: "#888", FIT_UP: "#3aa0ff", WELDED: "#f59e0b",
                   VISUAL_INSPECTED: "#84cc16", NDE_DONE: "#84cc16",
                   PWHT_DONE: "#22c55e", ACCEPTED: "#22c55e", REJECTED: "#ef4444" };
  const c = colors[status] || "#888";
  return <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 3, background: `${c}20`, color: c, border: `1px solid ${c}55` }}>{status}</span>;
}
